<script lang="ts" setup generic="T extends string | number | symbol">
import { computed, ref, watch } from 'vue';
import { onClickOutside } from '@vueuse/core';

import { AppSvgIcon } from '@libs/ui';

import SelectDropdown from '../common/SelectDropdown.vue';
import { AppFadeTransition } from '../../transitions';
import type {
  AppCustomSelectSize,
  AppCustomSelectType,
} from '../models/custom.model';

export interface AppCustomSelectProps {
  type: AppCustomSelectType;
  size?: AppCustomSelectSize;
  disabled?: boolean;
  placeholder?: string;
  selectedPlaceholder?: string;
  useTeleport?: boolean;
  wide?: boolean;
  isWrapperScrolling?: boolean;
}

const props = withDefaults(defineProps<AppCustomSelectProps>(), {
  placeholder: 'Select',
  useTeleport: true,
});

interface AppCustomSelectEmits {
  (event: 'clear-selection'): void;
}
const emit = defineEmits<AppCustomSelectEmits>();

const isDropdownOpened = ref(false);
const appCustomSelectRef = ref(null);
const appCustomSelectLabelRef = ref(null);

const hasSelectedOptions = computed(
  () => props.selectedPlaceholder !== undefined
);

function toggleDropdown(): void {
  if (props.disabled) {
    return;
  }

  isDropdownOpened.value = !isDropdownOpened.value;
}

function onClearSelection(): void {
  if (isDropdownOpened.value) {
    toggleDropdown();
  }
  emit('clear-selection');
}

onClickOutside(appCustomSelectRef, (event) => {
  const dropdown = document.querySelector('.app-select-dropdown');
  if (dropdown && dropdown.contains(event.target as Node)) {
    return;
  }
  if (isDropdownOpened.value) {
    isDropdownOpened.value = false;
  }
});

watch(
  () => props.isWrapperScrolling,
  () => {
    if (props.isWrapperScrolling) {
      isDropdownOpened.value = false;
    }
  }
);
</script>

<template>
  <div
    ref="appCustomSelectRef"
    class="app-custom-select"
    :class="[
      props.type,
      props.size,
      {
        wide: props.wide,
        opened: isDropdownOpened,
        selected: hasSelectedOptions,
      },
    ]"
  >
    <button
      ref="appCustomSelectLabelRef"
      class="app-custom-select-label"
      @click="toggleDropdown()"
    >
      <span class="app-custom-select-label-text">
        {{ props.selectedPlaceholder ?? props.placeholder }}
      </span>
      <AppSvgIcon
        v-if="isDropdownOpened && !hasSelectedOptions"
        name="chevronUp"
        size="18"
      />
      <AppSvgIcon
        v-if="!isDropdownOpened && !hasSelectedOptions"
        name="chevronDown"
        size="18"
      />
      <AppSvgIcon
        v-if="hasSelectedOptions"
        name="closeInCircle"
        size="18"
        @click.stop="onClearSelection"
      />
    </button>
    <template v-if="props.useTeleport">
      <Teleport v-if="isDropdownOpened" to="body">
        <AppFadeTransition transition-time="very-short">
          <SelectDropdown
            v-if="isDropdownOpened"
            :parent-ref="appCustomSelectLabelRef"
            :is-using-teleport="true"
          >
            <template #options>
              <slot :close="toggleDropdown" />
            </template>
          </SelectDropdown>
        </AppFadeTransition>
      </Teleport>
    </template>
    <template v-else>
      <SelectDropdown
        v-if="isDropdownOpened"
        :parent-ref="appCustomSelectLabelRef"
        :is-using-teleport="false"
      >
        <template #options>
          <slot :close="toggleDropdown" />
        </template>
      </SelectDropdown>
    </template>
  </div>
</template>

<style lang="scss" scoped>
@import '../../../styles/_media';
@import '../../../styles/_fonts.scss';

.app-custom-select {
  position: relative;
  display: flex;

  white-space: nowrap;

  align-items: center;

  * {
    box-sizing: border-box;
  }

  &.size-l {
    .app-custom-select-label {
      padding: var(--spacing-12) var(--spacing-16);
      @include font-label-m-bold;
    }
    .app-custom-select-dropdown {
      top: 44px;
    }
  }

  &.size-s {
    .app-custom-select-label {
      padding: var(--spacing-4) var(--spacing-8);
      @include font-label-s-light;
    }
    .app-custom-select-dropdown {
      top: 26px;
    }
  }

  &.primary {
    .app-custom-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-neutral-70);
    }
  }

  &.modern {
    .app-custom-select-label {
      background-color: var(--color-white);
      color: var(--color-neutral-70);

      border-radius: var(--border-radius-8);
      border: 1px solid var(--color-scrollbar);
      gap: var(--spacing-4);
      @include on-hover {
        background-color: var(--color-neutral-10);
        color: var(--color-neutral-70);
      }
    }
    &.opened {
      .app-custom-select-label {
        background-color: var(--color-white);
        border: 1px solid var(--color-soft-black);
      }
    }
    &.selected {
      .app-custom-select-label {
        background-color: var(--color-neutral-70);
        color: var(--color-white);
      }
    }
  }

  &.light {
    .app-custom-select-label {
      @include font-label-s-bold;
      background-color: var(--color-white);
      color: var(--color-neutral-70);
      padding: var(--spacing-8) var(--spacing-12);
      border-radius: var(--border-radius-8);
    }
  }

  &.wide {
    width: 100%;
    .app-custom-select-label {
      width: 100%;
    }
  }

  .app-custom-select-label {
    all: unset;
    display: flex;
    justify-content: space-between;
    align-items: center;
    cursor: pointer;

    padding: var(--spacing-8) var(--spacing-12);
    @include font-label-s-bold;
  }
}
</style>
